import clsx from "clsx";
import { FC, Fragment, MouseEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useRtl } from "@ryde/redux";
import {
  currencySignSelector,
  formatPrice,
  IPriceRateSearchCurrentRateInfo,
  IPriceRateSearchMatchedSummary,
  isValidPositiveNumber,
  RydeAppUnit,
  SINGLE_KEY,
  useDrawers,
} from "@ryde/shared";

import {
  resolvePriceRateSearchDisplayedPriceBreakdown,
  resolvePriceRateSearchMatchedRateInfo,
  resolvePriceRateSearchMatchStatus,
  resolvePriceRateSearchRateTitle,
  resolvePriceRateSearchTypeLabelKey,
  type TPriceRateSearchMatchStatus,
} from "./price-rate-search-logs.utils";

interface IPriceRateSearchEntityLinkProps {
  entity: RydeAppUnit;
  id: number;
  children: ReactNode;
}

const PriceRateSearchEntityLink: FC<IPriceRateSearchEntityLinkProps> = ({
  entity,
  id,
  children,
}) => {
  const { openEditFormDrawer } = useDrawers();
  const onClick = useEvent((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    void openEditFormDrawer({ entity, id });
  });

  return (
    <button
      className="price-rate-search-logs-header__entity-link"
      data-entity={entity}
      data-id={id}
      type="button"
      onClick={onClick}
    >
      {children}
    </button>
  );
};

const formatMoneyAmount = ({
  currencySign,
  value,
}: {
  currencySign: string;
  value: number;
}): string => `${currencySign}${formatPrice({ value })}`;

type TPriceRateSearchEntityLinkMode = "full" | "idOnly" | "plain";

const isNullishPriceValue = (
  value: number | null | undefined,
): value is null | undefined => value === null || value === undefined;

const renderPriceRateSearchRouteContent = ({
  rate,
  entityLinkMode,
  fromName,
  toName,
}: {
  rate: IPriceRateSearchCurrentRateInfo;
  entityLinkMode: TPriceRateSearchEntityLinkMode;
  fromName: string | null;
  toName: string | null;
}): ReactNode => {
  if (entityLinkMode === "idOnly") {
    return (
      <>
        {fromName ? <span>{fromName}</span> : null}
        {isValidPositiveNumber(rate.sourceAreaId) ? (
          <>
            {fromName ? " " : null}
            <PriceRateSearchEntityLink entity="area" id={rate.sourceAreaId}>
              {`#${rate.sourceAreaId}`}
            </PriceRateSearchEntityLink>
          </>
        ) : null}
        <span className="price-rate-search-chip__arrow"> → </span>
        {toName ? <span>{toName}</span> : null}
        {isValidPositiveNumber(rate.destinationAreaId) ? (
          <>
            {toName ? " " : null}
            <PriceRateSearchEntityLink
              entity="area"
              id={rate.destinationAreaId}
            >
              {`#${rate.destinationAreaId}`}
            </PriceRateSearchEntityLink>
          </>
        ) : null}
      </>
    );
  }
  if (entityLinkMode === "plain") {
    return (
      <>
        {fromName ||
          (isValidPositiveNumber(rate.sourceAreaId)
            ? `#${rate.sourceAreaId}`
            : null)}
        <span className="price-rate-search-chip__arrow"> → </span>
        {toName ||
          (isValidPositiveNumber(rate.destinationAreaId)
            ? `#${rate.destinationAreaId}`
            : null)}
      </>
    );
  }
  return (
    <>
      {isValidPositiveNumber(rate.sourceAreaId) ? (
        <PriceRateSearchEntityLink entity="area" id={rate.sourceAreaId}>
          {fromName || `#${rate.sourceAreaId}`}
        </PriceRateSearchEntityLink>
      ) : (
        fromName
      )}
      <span className="price-rate-search-chip__arrow"> → </span>
      {isValidPositiveNumber(rate.destinationAreaId) ? (
        <PriceRateSearchEntityLink entity="area" id={rate.destinationAreaId}>
          {toName || `#${rate.destinationAreaId}`}
        </PriceRateSearchEntityLink>
      ) : (
        toName
      )}
    </>
  );
};

const PriceRateSearchNamedInline: FC<{
  entity: RydeAppUnit;
  id?: number | null;
  name?: string | null;
  linkMode?: TPriceRateSearchEntityLinkMode;
}> = ({ entity, id, name, linkMode = "full" }) => {
  const hasId = isValidPositiveNumber(id);
  const trimmedName = name?.trim();
  if (!hasId && !trimmedName) {
    return null;
  }
  if (linkMode === "plain") {
    return (
      <span>
        {trimmedName || (hasId ? `#${id}` : null)}
        {hasId && trimmedName ? (
          <span className="price-rate-search-chip__id">{` #${id}`}</span>
        ) : null}
      </span>
    );
  }
  if (linkMode === "idOnly") {
    const idLink = hasId ? (
      <PriceRateSearchEntityLink entity={entity} id={id}>
        {`#${id}`}
      </PriceRateSearchEntityLink>
    ) : null;
    return (
      <span className="price-rate-search-named-inline">
        {trimmedName ? <span>{trimmedName}</span> : null}
        {hasId ? (
          <>
            {trimmedName ? " " : null}
            {idLink}
          </>
        ) : null}
      </span>
    );
  }
  const label = (
    <>
      {trimmedName || (hasId ? `#${id}` : null)}
      {hasId && trimmedName ? (
        <span className="price-rate-search-chip__id">{` #${id}`}</span>
      ) : null}
    </>
  );
  if (!hasId) {
    return <span>{label}</span>;
  }
  return (
    <PriceRateSearchEntityLink entity={entity} id={id}>
      {label}
    </PriceRateSearchEntityLink>
  );
};

const PriceRateSearchPriceLine: FC<{
  basePrice: number | null;
  additions: { label: string; value: number }[];
  total: number | null;
}> = ({ basePrice, additions, total }) => {
  const { t, i18n } = useTranslation();
  const { isRtl } = useRtl();
  const currencySign = useRecoilValue(currencySignSelector);
  const priceDir = isRtl ? "ltr" : undefined;
  const breakdown = resolvePriceRateSearchDisplayedPriceBreakdown({
    basePrice,
    additions,
    total,
  });
  const displayedBasePrice = breakdown.basePrice;
  const displayedTotal = breakdown.total;
  const displayedAdditions = breakdown.additions;
  if (
    isNullishPriceValue(displayedBasePrice) &&
    !displayedAdditions.length &&
    isNullishPriceValue(displayedTotal)
  ) {
    return null;
  }
  const resolveRowLabel = (label: string): string => {
    const labelKey = `ride.formLabel.${label}`;
    return i18n.exists(labelKey) ? t(labelKey) : label;
  };
  const singlePriceValue = displayedTotal ?? displayedBasePrice;
  if (!displayedAdditions.length && !isNullishPriceValue(singlePriceValue)) {
    return (
      <div className="price-rate-search-rate-card__price-line">
        <span className="price-rate-search-rate-card__fact-label">
          {`${t("ride.formLabel.price")}: `}
        </span>
        <span
          className="price-rate-search-rate-card__price-value"
          dir={priceDir}
        >
          {formatMoneyAmount({
            currencySign,
            value: singlePriceValue,
          })}
        </span>
      </div>
    );
  }
  const detailParts: string[] = [];
  if (!isNullishPriceValue(displayedBasePrice)) {
    detailParts.push(
      `${t("ride.formLabel.basePrice")}: ${formatMoneyAmount({
        currencySign,
        value: displayedBasePrice,
      })}`,
    );
  }
  displayedAdditions.forEach(row => {
    detailParts.push(
      `${resolveRowLabel(row.label)}: ${formatMoneyAmount({
        currencySign,
        value: row.value,
      })}`,
    );
  });
  if (isNullishPriceValue(displayedTotal)) {
    return null;
  }
  return (
    <div className="price-rate-search-rate-card__price-line">
      <span className="price-rate-search-rate-card__fact-label">
        {`${t("ride.priceRateSearchTotalPrice")}: `}
      </span>
      <span className="price-rate-search-rate-card__price-value" dir={priceDir}>
        {formatMoneyAmount({ currencySign, value: displayedTotal })}
      </span>
      {detailParts.length ? (
        <span className="price-rate-search-rate-card__price-details">
          {` (${detailParts.join(", ")})`}
        </span>
      ) : null}
    </div>
  );
};

const PriceRateSearchRateFacts: FC<{
  rate: IPriceRateSearchCurrentRateInfo;
  entityLinkMode?: TPriceRateSearchEntityLinkMode;
}> = ({ rate, entityLinkMode = "full" }) => {
  const { t } = useTranslation();
  const typeLabelKey = resolvePriceRateSearchTypeLabelKey(rate.type);
  const typeLabel = typeLabelKey ? t(`selectListLabel.${typeLabelKey}`) : null;
  const maxDuration = isValidPositiveNumber(rate.maxDuration)
    ? t("ride.priceRateSearchCurrentRateMinutes", {
        value: rate.maxDuration,
      })
    : null;
  const fromName = rate.sourceAreaName?.trim() || null;
  const toName = rate.destinationAreaName?.trim() || null;
  const hasRoute =
    isValidPositiveNumber(rate.sourceAreaId) ||
    isValidPositiveNumber(rate.destinationAreaId) ||
    Boolean(fromName || toName);
  const hasSupplier =
    isValidPositiveNumber(rate.supplierId) ||
    Boolean(rate.supplierName?.trim());
  const hasVehicleType =
    isValidPositiveNumber(rate.vehicleTypeId) ||
    Boolean(rate.vehicleTypeName?.trim());
  const facts: ReactNode[] = [];
  if (hasSupplier) {
    facts.push(
      <span key="supplier">
        <span className="price-rate-search-rate-card__fact-label">
          {`${t(`supplier.${SINGLE_KEY}`)}: `}
        </span>
        <PriceRateSearchNamedInline
          entity="supplier"
          id={rate.supplierId}
          linkMode={entityLinkMode}
          name={rate.supplierName}
        />
      </span>,
    );
  }
  if (hasVehicleType) {
    facts.push(
      <span key="vehicleType">
        <span className="price-rate-search-rate-card__fact-label">
          {`${t(`vehicleType.${SINGLE_KEY}`)}: `}
        </span>
        <PriceRateSearchNamedInline
          entity="vehicleType"
          id={rate.vehicleTypeId}
          linkMode={entityLinkMode}
          name={rate.vehicleTypeName}
        />
      </span>,
    );
  }
  if (typeLabel) {
    facts.push(<span key="type">{typeLabel}</span>);
  }
  if (maxDuration) {
    facts.push(
      <span key="duration">
        <span className="price-rate-search-rate-card__fact-label">
          {`${t("ride.priceRateSearchCurrentRateMaxDuration")}: `}
        </span>
        {maxDuration}
      </span>,
    );
  }
  if (hasRoute) {
    facts.push(
      <span key="route">
        <span className="price-rate-search-rate-card__fact-label">
          {`${t("ride.priceRateSearchRoute")}: `}
        </span>
        {renderPriceRateSearchRouteContent({
          rate,
          entityLinkMode,
          fromName,
          toName,
        })}
      </span>,
    );
  }
  if (!facts.length) {
    return null;
  }
  return (
    <div className="price-rate-search-rate-card__facts">
      {facts.map((fact, index) => (
        <Fragment key={index}>
          {index > 0 ? ", " : null}
          {fact}
        </Fragment>
      ))}
    </div>
  );
};

const PriceRateSearchRateCard: FC<{
  rate: IPriceRateSearchCurrentRateInfo;
  variant: "current" | "winner";
  matchStatus: TPriceRateSearchMatchStatus;
  showPrices: boolean;
  breakdown: IPriceRateSearchMatchedSummary | null;
}> = ({ rate, variant, matchStatus, showPrices, breakdown }) => {
  const { t } = useTranslation();
  const title = resolvePriceRateSearchRateTitle({
    name: rate.name,
    label: rate.label,
  });
  const priceLine = showPrices ? (
    <PriceRateSearchPriceLine
      additions={breakdown?.additions ?? []}
      basePrice={breakdown?.basePrice ?? rate.minPrice}
      total={breakdown?.price ?? rate.minPrice ?? null}
    />
  ) : null;

  const isInfoPlate = variant === "current";
  const entityLinkMode: TPriceRateSearchEntityLinkMode = "idOnly";

  const renderSectionTitle = (): ReactNode => {
    if (isInfoPlate) {
      return (
        <span className="price-rate-search-rate-card__section-title">
          {t("ride.priceRateSearchCurrent")}
        </span>
      );
    }
    if (matchStatus === "current-is-winner") {
      return (
        <span
          className={clsx(
            "price-rate-search-rate-card__section-title",
            "price-rate-search-rate-card__section-title--winner",
          )}
        >
          {t("ride.priceRateSearchCurrentIsWinner")}
        </span>
      );
    }
    return (
      <span
        className={clsx(
          "price-rate-search-rate-card__section-title",
          "price-rate-search-rate-card__section-title--winner",
        )}
      >
        {t("ride.priceRateSearchWinner")}
      </span>
    );
  };

  return (
    <div
      className={clsx(
        "price-rate-search-rate-card",
        isInfoPlate && "price-rate-search-rate-card--info",
        variant === "winner" && "price-rate-search-rate-card--winner",
      )}
    >
      <div className="price-rate-search-rate-card__main">
        <div className="price-rate-search-rate-card__info">
          <div className="price-rate-search-rate-card__top">
            {renderSectionTitle()}
          </div>
          <div className="price-rate-search-rate-card__title">
            {title ? (
              <span className="price-rate-search-rate-card__title-text">
                {title}
              </span>
            ) : null}
            {title ? " " : null}
            <PriceRateSearchEntityLink entity="priceRate" id={rate.id}>
              {`#${rate.id}`}
            </PriceRateSearchEntityLink>
          </div>
          <PriceRateSearchRateFacts
            entityLinkMode={entityLinkMode}
            rate={rate}
          />
          {priceLine}
        </div>
      </div>
    </div>
  );
};

interface IPriceRateSearchRatesBlocksProps {
  currentPriceRate: IPriceRateSearchCurrentRateInfo | null;
  matchedPriceRate: IPriceRateSearchMatchedSummary | null;
  matchedPriceRateId: number | null;
  isSearchSettled: boolean;
  showPrices: boolean;
}

const resolvePriceRateSearchRatesBlocks = ({
  currentPriceRate,
  matchedPriceRate,
  matchedPriceRateId,
  isSearchSettled,
}: IPriceRateSearchRatesBlocksProps): {
  matchStatus: TPriceRateSearchMatchStatus;
  winnerRate: IPriceRateSearchCurrentRateInfo | null;
  showWinnerCard: boolean;
} => {
  const matchStatus = resolvePriceRateSearchMatchStatus({
    isSearchSettled,
    currentPriceRateId: currentPriceRate?.id,
    matchedPriceRateId,
  });
  const winnerRate = resolvePriceRateSearchMatchedRateInfo(matchedPriceRate);
  const showWinnerCard =
    matchStatus === "different-winner" && winnerRate !== null;
  return { matchStatus, winnerRate, showWinnerCard };
};

export const PriceRateSearchCurrentRateBlock: FC<
  IPriceRateSearchRatesBlocksProps
> = ({
  currentPriceRate,
  matchedPriceRate,
  matchedPriceRateId,
  isSearchSettled,
  showPrices,
}) => {
  const { matchStatus } = resolvePriceRateSearchRatesBlocks({
    currentPriceRate,
    matchedPriceRate,
    matchedPriceRateId,
    isSearchSettled,
    showPrices,
  });
  if (!currentPriceRate) {
    return null;
  }
  const currentBreakdown =
    matchedPriceRate?.id === currentPriceRate.id ? matchedPriceRate : null;
  return (
    <PriceRateSearchRateCard
      breakdown={currentBreakdown}
      matchStatus={matchStatus}
      rate={currentPriceRate}
      showPrices={showPrices}
      variant="current"
    />
  );
};

export const PriceRateSearchWinnerRateBlock: FC<
  IPriceRateSearchRatesBlocksProps
> = ({
  currentPriceRate,
  matchedPriceRate,
  matchedPriceRateId,
  isSearchSettled,
  showPrices,
}) => {
  const { t } = useTranslation();
  const { matchStatus, winnerRate, showWinnerCard } =
    resolvePriceRateSearchRatesBlocks({
      currentPriceRate,
      matchedPriceRate,
      matchedPriceRateId,
      isSearchSettled,
      showPrices,
    });
  const showFoundCard =
    matchStatus === "current-is-winner" && currentPriceRate !== null;
  if (!showWinnerCard && !showFoundCard && matchStatus !== "not-found") {
    return null;
  }
  const foundRate =
    matchStatus === "current-is-winner" ? currentPriceRate : winnerRate;
  return (
    <div className="price-rate-search-rates">
      {matchStatus === "not-found" ? (
        <span className="price-rate-search-logs-header__result price-rate-search-logs-header__result--empty">
          {t("ride.priceRateSearchNotFound")}
        </span>
      ) : null}
      {foundRate ? (
        <PriceRateSearchRateCard
          breakdown={matchedPriceRate}
          matchStatus={matchStatus}
          rate={foundRate}
          showPrices={showPrices}
          variant="winner"
        />
      ) : null}
    </div>
  );
};
